<template>
  <div id="trading-steps">
    <v-stepper class="pa-0" v-model="stepper" vertical non-linear :key="key">
      <v-stepper-header
        class="pa-4 pa-md-8 p-stepper-header"
        @click="expandSteps()"
      >
        <v-row class="stepper-row" align="center" no-gutters>
          <v-col cols="12" md="1" align="center">
            <v-icon class="p-stepper-icon">mdi-account-cog</v-icon>
          </v-col>
          <v-col cols="12" md="6" lg="7">
            <h2 class="g-heading-l p-stepper-title">
              {{ readyToTrade ? 'Ready to Trade' : 'Trading Setup Required' }}
            </h2>
          </v-col>
          <v-col cols="12" md="3" lg="2" class="text-center mb-2 mb-md-0">
            <i-instructions
              instructionKey="dashboard"
              :disregardSections="disregardInstructionSections"
              @open="showSteps = !showSteps"
            >
            </i-instructions>
          </v-col>
          <v-col cols="12" md="2" class="text-center pl-md-4">
            <template v-if="$vuetifyDisplay.mdAndUp">
              <template v-if="showSteps">
                <v-icon class="p-arrow-icon">mdi-arrow-up</v-icon>
              </template>
              <template v-else>
                <v-icon class="p-arrow-icon">mdi-arrow-down</v-icon>
              </template>
            </template>
            <v-btn
              class="i-btn-block-primary"
              rounded
              @click="showSteps = !showSteps"
              v-else
            >
              <template v-if="showSteps">
                <v-icon left class="mt-1">mdi-arrow-up</v-icon>
                Hide details
              </template>
              <template v-else>
                <v-icon left class="mt-1">mdi-arrow-down</v-icon>
                Show details
              </template>
            </v-btn>
          </v-col>
        </v-row>
      </v-stepper-header>

      <template v-if="showSteps">
        <v-row class="ma-4 ma-sm-6 ma-md-8" v-if="readyToTrade">
          <v-col cols="12">
            <div class="p-ready-message">
              <div class="pa-0 ma-0">
                <ul>
                  <li>
                    Congratulations you are successfully set up and ready to
                    trade
                  </li>
                  <li>Add optional tasks now or at any time</li>
                  <li>
                    Maximise the portal using User Guide tab or Instructions
                    buttons at any time
                  </li>
                </ul>
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row class="ma-4 ma-sm-6 ma-md-8" v-else>
          <v-col cols="12">
            <div class="p-not-ready-message">
              <div class="pa-0 ma-0">
                <h5 class="mb-2">READY TO TRADE INSTRUCTIONS</h5>
                <ol>
                  <li>
                    Complete all <strong>Steps</strong> (in order) to begin
                    trading – each step will change from red to green once
                    complete
                  </li>
                  <li>
                    <strong>Optional</strong> steps can be setup initially or at
                    a later date
                  </li>
                  <li>
                    <strong>Promotions</strong> can be added (on Live products
                    only) once at least one product is Live
                  </li>
                </ol>
              </div>
            </div>
          </v-col>
        </v-row>

        <v-row class="ma-4 ma-sm-6 ma-md-8">
          <v-col cols="12">
            <h3 class="g-heading-m">Mandatory</h3>
            <p>
              <template v-if="featureSubscriptionEnabled"
                >Complete steps 1 to 4 in any order then step 5</template
              >
              <template v-else
                >Complete steps 1 to {{ mandatoryTradingSteps.length - 1 }} in
                any order then step
                {{ mandatoryTradingSteps.length }}
              </template>
            </p>
          </v-col>

          <v-col
            v-for="(step, index) in mandatoryTradingSteps"
            :key="step.title"
            cols="12"
            sm="6"
            class="px-3 py-2"
          >
            <v-stepper-step
              class="p-step"
              color="green"
              :class="{
                'p-step-complete': step.isComplete,
                'p-step-incomplete': !step.isComplete
              }"
              :rules="[() => step.isComplete]"
              :complete="step.isComplete"
              @click="step.action()"
            >
              <div>
                <h4 class="g-heading-xs">
                  Step {{ index + 1 }} - {{ step.title }}
                </h4>
                <span>{{ step.subText }}</span>
              </div>
            </v-stepper-step>
          </v-col>
        </v-row>

        <v-row class="ma-4 ma-sm-6 ma-md-8" v-if="readyForOptionalSteps">
          <v-col cols="12">
            <h3>Optional</h3>
            <p>Setup now or at any time</p>
          </v-col>

          <v-col
            v-for="step in optionalTradingSteps"
            :key="step.title"
            cols="12"
            sm="6"
            class="px-3 py-2"
          >
            <div
              class="p-step p-step-optional d-flex align-center"
              @click="$router.push(step.routeTo)"
            >
              <v-icon class="g-heading-m mr-3" color="primary" left
                >mdi-check-circle</v-icon
              >
              <div>
                <h4 class="g-heading-xs">
                  {{ step.title }}
                  <template v-if="step.isPremium"> (* Premium only)</template>
                </h4>
                <span>{{ step.subText }}</span>
              </div>
            </div>
          </v-col>
        </v-row>

        <v-row class="ma-4 ma-sm-6 ma-md-8" v-if="readyForOptionalSteps">
          <v-col cols="12">
            <h3>Promotions</h3>
            <p>
              {{
                readyForPromotionalSteps
                  ? 'Now available as product(s) are LIVE'
                  : 'Will become available once products are LIVE at midnight'
              }}
            </p>
          </v-col>

          <v-col
            v-for="step in promotionTradingSteps"
            :key="step.title"
            cols="12"
            sm="6"
            class="px-3 py-2"
          >
            <div
              class="p-step d-flex align-center"
              :class="{
                'p-step-optional': readyForPromotionalSteps,
                'p-step-disabled': !readyForPromotionalSteps
              }"
              @click="
                readyForPromotionalSteps ? $router.push(step.routeTo) : null
              "
            >
              <v-icon class="g-heading-m mr-3" color="primary" left
                >mdi-check-circle</v-icon
              >
              <div>
                <h4 class="g-heading-xs">
                  {{ step.title }}
                  <template v-if="step.isPremium"> (* Premium only)</template>
                </h4>
                <span>{{ step.subText }}</span>
              </div>
            </div>
          </v-col>
        </v-row>
      </template>
    </v-stepper>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import iInstructions from '@/components/Instructions'

export default {
  components: {
    iInstructions
  },

  data() {
    return {
      key: 0,
      showSteps: false,
      stepper: 0
    }
  },

  computed: {
    ...mapGetters('products', ['hasAnyProducts']),
    ...mapGetters('freightInfo', ['validTradingRules']),
    ...mapGetters('tenant', [
      'featureSubscriptionEnabled',
      'featureCreditCardPaymentEnabled',
      'featureBankAccountsEnabled',
      'featureSupplierDirectRebatesEnabled',
      'featureCouponsEnabled',
      'userGuideEnabled'
    ]),

    ...mapGetters('profile', [
      'readyForOptionalSteps',
      'readyForPromotionalSteps',
      'readyToTrade',
      'readyToTradeLoaded',
      'hasValidSubscription',
      'userGuideRead',
      'bankDetailsAvailable'
    ]),

    disregardInstructionSections() {
      if (!this.featureSubscriptionEnabled) {
        return ['Subscription']
      }

      return []
    },

    mandatoryTradingSteps() {
      return [
        {
          title: 'Review User Guide',
          subText: 'Click to go to User Guide',
          action: () => {
            this.$router.push({ name: 'user-guide' })
          },
          isComplete: !this.userGuideEnabled || this.userGuideRead
        },
        {
          title: 'Set up Subscription',
          subText: 'Click to go to Subscriptions',
          action: () => {
            this.$router.push({ name: 'subscription' })
          },
          isComplete: this.hasValidSubscription,
          enabled: this.featureSubscriptionEnabled
        },
        {
          title: 'Setup Bank Account for Payout',
          subText: 'Click to edit Bank Account Details',
          action: () => {
            this.$router.push({ name: 'bank-details' })
          },
          isComplete: this.bankDetailsAvailable,
          enabled: this.featureBankAccountsEnabled
        },
        {
          title: 'Add Supplier Trading Rules',
          subText: 'Click to go to Supplier Trading Rules',
          action: () => {
            this.$router.push({ name: 'trading-rules' })
          },
          isComplete: this.validTradingRules
        },
        {
          title: 'Add Products',
          subText: 'Click to go to Products',
          action: () => {
            this.$router.push({ name: 'products' })
          },
          isComplete: this.hasAnyProducts
        }
      ].filter((e) => e.enabled !== false)
    },

    optionalTradingSteps() {
      return [
        {
          title: 'Rebates',
          subText: 'Click to go to Rebates',
          routeTo: { name: 'rebates' },
          isPremium: false,
          enabled: this.featureSupplierDirectRebatesEnabled
        },
        {
          title: 'Coupons',
          subText: 'Click to go to Coupons',
          routeTo: { name: 'coupons' },
          isPremium: false,
          enabled: this.featureCouponsEnabled
        },
        {
          title: 'Logos & Brand Images',
          subText: 'Click to go to Logos & Brand Images',
          routeTo: { name: 'brand-logos' },
          isPremium: this.featureSubscriptionEnabled
        },
        {
          title: 'Multiple Account Users',
          subText: 'Click to go to Multiple Account Users',
          routeTo: { name: 'users-management' },
          isPremium: this.featureSubscriptionEnabled
        }
      ].filter((e) => e.enabled !== false)
    },

    promotionTradingSteps() {
      return [
        {
          title: 'Family Groups',
          subText: 'Click to go to Family Groups',
          routeTo: { name: 'family-groups' },
          isPremium: false
        },
        {
          title: 'Price Drops',
          subText: 'Click to go to Price Drops',
          routeTo: { name: 'price-drops' },
          isPremium: false
        },
        {
          title: 'Specials',
          subText: 'Click to go to Specials',
          routeTo: { name: 'specials' },
          isPremium: false
        },
        {
          title: 'Volume Buys',
          subText: 'Click to go to Volume Buys',
          routeTo: { name: 'volume-buys' },
          isPremium: this.featureSubscriptionEnabled
        }
      ].filter((e) => e.enabled !== false)
    }
  },

  methods: {
    expandSteps() {
      if (this.$vuetifyDisplay.mdAndUp) this.showSteps = !this.showSteps
    },

    setStepsVisibility() {
      if (this.$route.query?.showTradingSteps) {
        this.showSteps = true
      } // Used when just completed trading steps to see success message
      else if (this.readyToTradeLoaded) this.showSteps = !this.readyToTrade
    }
  },

  mounted() {
    this.setStepsVisibility()
  },

  watch: {
    readyToTradeLoaded() {
      this.setStepsVisibility()
    },

    readyToTrade() {
      this.setStepsVisibility()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.v-stepper--vertical .v-stepper__step {
  padding: 0px !important;
}

#trading-steps {
  .p-step {
    border: 2px solid $primary-color;
    border-radius: 5px;
    color: $primary-color;
    padding: 8px 16px !important;

    &:hover {
      cursor: pointer;
    }

    &-complete {
      background-color: $green-color-bg-lighter-1;
      border: 2px solid $green-color;

      * {
        color: green !important;
      }

      &:hover {
        background-color: $green-color-bg;
      }
    }

    &-incomplete {
      background-color: $red-color-bg-lighter-1;
      border: 2px solid $red-color;

      * {
        color: red !important;
      }

      &:hover {
        background-color: $red-color-bg;
      }
    }

    &-disabled {
      background-color: $gray-color-bg;
      border: 2px solid $gray-color;

      * {
        color: $gray-color !important;
      }

      &:hover {
        cursor: auto;
      }
    }

    &-optional {
      background-color: var(--v-lightHighlightBackground-lighten1);
      border: 2px solid $primary-color;

      * {
        color: $primary-color !important;
      }

      &:hover {
        background-color: $blue-color-bg;
      }
    }
  }

  .link {
    color: $secondary-color;
    text-decoration: none;
  }

  .p-arrow-icon {
    color: $primary-color;

    &:before {
      font-size: 42px;
    }
  }

  .p-completed {
    color: green;
  }

  .p-div-options {
    line-height: 2.75em;
  }

  .p-ready-message,
  .p-not-ready-message {
    padding: 7% 5%;
    border-radius: 5px;
    line-height: 2em;
  }

  .p-ready-message {
    background-color: $green-color-bg;
  }

  .p-not-ready-message {
    background-color: $blue-color-bg;
  }

  .p-stepper-header {
    background-color: white;
    cursor: pointer;
    min-height: 270px;
    border-bottom: 5px solid $primary-color;
  }

  .p-stepper-icon {
    color: $primary-color;

    &:before {
      font-size: 42px;
    }
  }

  .p-stepper-title {
    text-align: center;
  }

  .p-optional-step-div {
    padding: 0px 16px;
    margin-bottom: 8px;

    &:hover {
      cursor: pointer;
    }
  }
}

@media only screen and (min-width: 600px) {
  #trading-steps {
    .p-ready-message,
    .p-not-ready-message {
      padding: 5% 5%;
    }
  }
}

@media only screen and (min-width: 960px) {
  #trading-steps {
    .p-ready-message,
    .p-not-ready-message {
      line-height: 2.25em;
      padding: 2% 5%;

      li {
        font-size: 20px;
      }
    }

    .p-stepper-header {
      min-height: 125px;
    }

    .p-stepper-icon {
      &:before {
        font-size: 52px;
      }
    }

    .p-stepper-title {
      text-align: left;
      margin-left: 20px;
    }
  }
}
</style>
